import { arrayUtils } from '@sato/components';
import {
  addDays,
  addMonths,
  endOfDay,
  endOfMonth,
  endOfToday,
  isAfter,
  isSameDay,
  max,
  max as maxDate,
  startOfDay,
} from 'date-fns';
import { includes } from 'lodash';
import { boolean, object, string } from 'yup';

import { ParkingSpaceContractItem } from '@hooks/useParkingSpaceContracts';
import { ContractStatusCode } from '@models/contract.types';
import {
  ParkingArea,
  ParkingProduct,
  ParkingRentingPartyType,
  ParkingSpace,
  SpecialParkingSpaceType,
} from '@models/parking.types';
import { RealEstateDocument } from '@models/realEstate.types';
import { contractStore } from '@stores/contractStore';
import { TranslatorFn } from '@stores/localization';
import { userStore } from '@stores/userStore';

import { PARKING_RENTING_RANGE_DAYS } from './parking.constants';

/** Returns true if the real estate has parking space products available directly,
 *  or via linked parking areas in the neighbourhood.
 */
export const realEstateHasParkingSpaceProducts = (
  realEstate: RealEstateDocument | undefined
): boolean =>
  !!realEstate?.totalNumberOfParkingSpaceProducts ||
  !!realEstate?.linkedParkingAreas?.length;

export const getPossibleParkingStartDateRange = (
  freeDate: Date
): {
  firstPossibleStartDate: Date;
  lastPossibleStartDate: Date;
  isOnlyOnePossibleStartDate: boolean;
} => {
  /*
   * The first possible start date is...
   * ...tomorrow if freedate is today or in the past
   * ...same as the freedate if it's after today
   */
  const firstPossibleStartDate =
    freeDate && isAfter(freeDate, endOfToday())
      ? freeDate
      : addDays(new Date(), 1);

  /*
   * Last possible start date is either 45 days from now or the freedate, whichever comes later.
   */
  const lastPossibleStartDate = maxDate([
    firstPossibleStartDate,
    addDays(new Date(), PARKING_RENTING_RANGE_DAYS),
  ]);

  return {
    firstPossibleStartDate: startOfDay(firstPossibleStartDate),
    lastPossibleStartDate: endOfDay(lastPossibleStartDate),
    /** Whether there is only one possible start date (ie. first and last dates are on the same day) */
    isOnlyOnePossibleStartDate: isSameDay(
      firstPossibleStartDate,
      lastPossibleStartDate
    ),
  };
};

/**
 * Gets end date for the old, and start date for the new parking contract. These do not depend on
 * parking product information, but are always the end of the next month, and a day after that respectively.
 *
 * @docs https://satocorporation.atlassian.net/wiki/spaces/SW/pages/5490311179/Autopaikan+irtisanominen+ja+vaihtaminen#Autopaikan-vaihtaminen
 */
export const getDatesWhenParkingSpacesCanBeSwitched = (): {
  newContractStartDate: Date;
  oldContractEndDate: Date;
} => {
  // End date is always the last day of next month.
  const oldContractEndDate = endOfMonth(addMonths(new Date(), 1));

  return {
    oldContractEndDate: endOfDay(oldContractEndDate),
    newContractStartDate: startOfDay(addDays(oldContractEndDate, 1)),
  };
};

/** Renting party keys that can be compared against when assessing whether a renting company is SATO */
export const satoRentingPartyTypes = [
  'SATO_CUSTOMER_SERVICE',
  'SATO_RENTING_HELSINKI',
  'SATO_RENTING_TAMPERE',
  'SATO_RENTING_TURKU',
] as ParkingRentingPartyType[];

/**
 * Returns true if the user is allowed to manage the given parking contract, e.g. terminate it.
 */
export const isEligibleToManageParkingContract = (
  parkingSpaceContractItem: ParkingSpaceContractItem
) =>
  // parking space is not included in rent
  parkingSpaceContractItem.invoiceType !== 'includedInRent' &&
  // they are a party in the apartment contract
  contractStore.isUserContractParty &&
  // they are a party in the parking contract
  !!parkingSpaceContractItem.renterContactIds?.some(
    (contactId) => contactId === userStore.user?.crmID
  ) &&
  // contract is not fixed
  parkingSpaceContractItem.parkingContractType !== 'FIXED_PERIOD_TYPE' &&
  // contract is active
  parkingSpaceContractItem.contractStatus === ContractStatusCode.ACTIVE_STATUS;

/** Returns true if the user is allowed to switch the given parking contract to another parking space */
export const canSwitchParkingSpace = (
  parkingSpaceContractItem: ParkingSpaceContractItem
) =>
  isEligibleToManageParkingContract(parkingSpaceContractItem) &&
  // Switching is only supported for parking spaces invoiced as part of the apartment contract
  parkingSpaceContractItem.invoiceType === 'invoicedInContract' &&
  satoRentingPartyTypes.includes(parkingSpaceContractItem.rentingParty);

/** Returns true if parking area renting is managed by SATO */
export const isSATOParkingRentingParty = (parkingArea: ParkingArea) =>
  satoRentingPartyTypes.includes(parkingArea.parkingRentingParty);

/** Returns true if electric car charging in the parking area is billed by eParking (IGL-Technologies Oy) */
export const isChargerBillingCompanyEParking = (
  parkingArea: Pick<ParkingArea, 'chargerBillingCompany'>
) => !!parkingArea.chargerBillingCompany?.name.includes('eParking');

/**
 * Returns a reminder to cancel the electric car charging service separately when terminating
 * a parking contract, or `null` if the parking space is not an electric car charging space.
 */
export const getChargingServiceTerminationReminder = (
  {
    parkingSpace,
    parkingArea,
  }: {
    parkingSpace: Pick<ParkingSpace, 'electricCarCharge'>;
    parkingArea: Pick<ParkingArea, 'chargerBillingCompany'>;
  },
  t: TranslatorFn
): string | null => {
  if (!parkingSpace.electricCarCharge) return null;

  return isChargerBillingCompanyEParking(parkingArea)
    ? t('parking.terminate_charging_service_reminder_eParking')
    : t('parking.terminate_charging_service_reminder_other');
};

/** Returns true if parking space is a quota space (kiintiöpaikka) */
export const isQuotaParkingSpace = (parkingSpace: ParkingSpace) =>
  includes<SpecialParkingSpaceType | null>(
    ['UNNUMBERED_QUOTA_PARKING_SPACE', 'QUOTA_PARKING_SPACE'],
    parkingSpace.specialParkingSpace
  );

/** Returns parking start date validation schema */
export const getParkingSpaceStartDateSchema = (
  laterStartDate: Date,
  t: (key: string) => string
) => {
  const missingError = t('error.required_field') ?? 'error!';
  const invalidDateError = t('error.invalid_date') ?? 'error!';

  const { firstPossibleStartDate, lastPossibleStartDate } =
    getPossibleParkingStartDateRange(laterStartDate);

  return string()
    .serializedPlainDate(invalidDateError)
    .minSerializedDate(firstPossibleStartDate, invalidDateError)
    .maxSerializedDate(lastPossibleStartDate, invalidDateError)
    .required(missingError);
};

/** Returns parking renting form validation schema */
export const getParkingSpaceRentingFormSchema = ({
  laterStartDate,
  t,
}: {
  laterStartDate: Date;
  t: (key: string) => string;
}) => {
  return object({
    startDate: getParkingSpaceStartDateSchema(laterStartDate, t),
    agreeTos: boolean().isTrue().required(),
  });
};

/** Get translated comma-separated parking space features */
export const getParkingSpaceFeaturesText = (
  { heatPole, electricCarCharge, chargerPlugType, chargerPower }: ParkingSpace,
  t: TranslatorFn
): string =>
  arrayUtils.joinArrayString([
    heatPole && t(`parking.heatPole`),
    electricCarCharge && t('parking.electricCarCharge'),
    chargerPlugType && t(`parking.chargerPlugType.${chargerPlugType}`),
    chargerPower && t(`parking.chargerPowerType.${chargerPower}`),
  ]);

export const getParkingProductAvailableDate = (
  parkingProduct?: ParkingProduct | null
) =>
  max([
    new Date(parkingProduct?.freeDate ?? 0),
    new Date(contractStore.selectedContract?.dateOfStartingRent ?? 0),
  ]);

export const isSharedCarParkingSpace = (parkingSpace: ParkingSpace) =>
  parkingSpace.specialParkingSpace === 'SHARED_CAR_PARKING_SPACE' ||
  parkingSpace.specialParkingSpace ===
    'SHARED_CAR_PARKING_SPACE_AND_SHARED_CAR';

export const getSharedCarDescription = (
  parkingSpace: ParkingSpace,
  language: string
) =>
  language === 'fi'
    ? parkingSpace.sharedCarDescriptionFi
    : parkingSpace.sharedCarDescriptionEn;
